/**
 * ExplosionMarker - Animated strike marker with CSS animations.
 * All animation timings are in WALL-CLOCK time (independent of replay speed).
 * Shows: expanding shockwave rings, flash, info cartouche (city + source for 3s).
 */
import React, { useEffect, useState, useRef } from 'react';
import { Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';

const FLAG_CODES = {
  'United States': 'us', 'Iran': 'ir', 'Israel': 'il', 'France': 'fr',
  'Russia': 'ru', 'Turkey': 'tr', 'Saudi Arabia': 'sa', 'UAE': 'ae',
  'United Kingdom': 'gb', 'Germany': 'de',
};
const FLAG_LABELS = {
  'United States': 'USA', 'Iran': 'IRAN', 'Israel': 'ISRAEL', 'France': 'FRANCE',
  'Russia': 'RUSSIE', 'Turkey': 'TURQUIE', 'Saudi Arabia': 'ARABIE S.',
  'United Kingdom': 'UK', 'UAE': 'EAU',
};

function formatStrikeTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

// ============ PHASE 1 & 2: Active explosion with shockwaves ============
function createExplosionIcon(attribution, datetime, city) {
  const flagCode = attribution?.origin ? FLAG_CODES[attribution.origin] : null;
  const flagLabel = attribution?.origin ? (FLAG_LABELS[attribution.origin] || attribution.origin) : null;
  const timeStr = formatStrikeTime(datetime);
  const flagImg = flagCode
    ? `<img src="https://flagcdn.com/w40/${flagCode}.png" style="width:28px;height:18px;border-radius:2px;border:1px solid rgba(255,255,255,0.4);vertical-align:middle;" />`
    : '';

  const attrLine = flagLabel
    ? `${flagImg} <span style="color:#ff6644;font-family:monospace;font-size:14px;font-weight:bold;letter-spacing:1px;">${flagLabel}</span>`
    : '';

  return L.divIcon({
    className: 'explosion-container',
    html: `
      <div class="explosion-marker">
        <div class="explosion-flash"></div>
        <div class="explosion-ring1"></div>
        <div class="explosion-ring2"></div>
        <div class="explosion-ring3"></div>
        <div class="explosion-core"></div>
      </div>
    `,
    iconSize: [300, 300],
    iconAnchor: [150, 150],
  });
}

// ============ INFO CARTOUCHE: City + Source (shown for 3 seconds) ============
function createInfoCartoucheIcon(city, sourceInfo, attribution, datetime) {
  const flagCode = attribution?.origin ? FLAG_CODES[attribution.origin] : null;
  const flagLabel = attribution?.origin ? (FLAG_LABELS[attribution.origin] || attribution.origin) : null;
  const timeStr = formatStrikeTime(datetime);
  const flagImg = flagCode
    ? `<img src="https://flagcdn.com/w40/${flagCode}.png" style="width:24px;height:16px;border-radius:2px;border:1px solid rgba(255,255,255,0.3);vertical-align:middle;margin-right:6px;" />`
    : '';

  const cityName = city || 'Zone inconnue';
  const source = sourceInfo || 'FIRMS VIIRS';

  return L.divIcon({
    className: 'info-cartouche-container',
    html: `
      <div class="info-cartouche">
        <div class="info-cartouche-header">
          <span class="info-cartouche-icon">&#x26A0;</span>
          FRAPPE DETECTEE
        </div>
        <div class="info-cartouche-body">
          <div class="info-cartouche-city">${cityName}</div>
          <div class="info-cartouche-meta">
            ${flagImg}${flagLabel ? `<span class="info-cartouche-flag">${flagLabel}</span>` : ''}
            <span class="info-cartouche-time">${timeStr}</span>
          </div>
          <div class="info-cartouche-source">Source: ${source}</div>
        </div>
      </div>
    `,
    iconSize: [280, 130],
    iconAnchor: [140, 150],
  });
}

// ============ PHASE 3+: Persistent strike mark ============
function createPersistentStrikeIcon(attribution, datetime, city) {
  const flagCode = attribution?.origin ? FLAG_CODES[attribution.origin] : null;
  const flagLabel = attribution?.origin ? (FLAG_LABELS[attribution.origin] || attribution.origin) : null;
  const timeStr = formatStrikeTime(datetime);
  const cityShort = city ? city.split(' (')[0] : '';
  const flagImg = flagCode
    ? `<img src="https://flagcdn.com/w20/${flagCode}.png" style="width:18px;height:12px;border-radius:2px;vertical-align:middle;margin-right:4px;" />`
    : '';

  const labelParts = [];
  if (flagImg) labelParts.push(flagImg);
  if (flagLabel) labelParts.push(`<span style="color:#ff8866;font-weight:bold;">${flagLabel}</span>`);
  if (cityShort) labelParts.push(`<span style="color:#ffcc88;">${cityShort}</span>`);
  if (timeStr) labelParts.push(`<span style="color:#ffaa66;font-size:11px;">${timeStr}</span>`);

  return L.divIcon({
    className: 'strike-persistent',
    html: `
      <div class="strike-mark">
        <div class="strike-dot"></div>
        <div class="strike-info">
          ${labelParts.join(' ')}
        </div>
      </div>
    `,
    iconSize: [200, 50],
    iconAnchor: [12, 12],
  });
}

export default function ExplosionMarker({ lat, lon, attribution, isActive, aircraftPos, datetime, city, sourceInfo }) {
  // Phases: 0=missile, 1=flash+rings, 2=cartouche, 3=persistent
  const [phase, setPhase] = useState(0);
  const [showMissile, setShowMissile] = useState(false);
  const [missilePos, setMissilePos] = useState(null);
  const [showCartouche, setShowCartouche] = useState(false);
  const missileRef = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => {
    // Cleanup all timers on unmount
    return () => {
      timersRef.current.forEach(t => clearTimeout(t));
      if (missileRef.current) clearInterval(missileRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isActive) { setPhase(0); setShowMissile(false); setShowCartouche(false); return; }

    // Clear previous timers
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];

    const addTimer = (fn, delay) => {
      const t = setTimeout(fn, delay);
      timersRef.current.push(t);
    };

    // All timings in WALL-CLOCK milliseconds (independent of replay speed)
    if (aircraftPos) {
      // Phase 0: Missile inbound (1.5 seconds)
      setShowMissile(true);
      let step = 0;
      const totalSteps = 25;
      missileRef.current = setInterval(() => {
        step++;
        const t = step / totalSteps;
        setMissilePos([
          aircraftPos[0] + (lat - aircraftPos[0]) * t,
          aircraftPos[1] + (lon - aircraftPos[1]) * t,
        ]);
        if (step >= totalSteps) {
          clearInterval(missileRef.current);
          setShowMissile(false);
          setMissilePos(null);
          // Explosion starts
          setPhase(1);
          setShowCartouche(true);
          addTimer(() => setPhase(2), 2500);       // Rings finish at 2.5s
          addTimer(() => setShowCartouche(false), 3000); // Cartouche visible 3s
          addTimer(() => setPhase(3), 4000);        // Persistent mark at 4s
        }
      }, 60);
    } else {
      // No missile - direct explosion
      setPhase(1);
      setShowCartouche(true);
      addTimer(() => setPhase(2), 2500);
      addTimer(() => setShowCartouche(false), 3000);
      addTimer(() => setPhase(3), 4000);
    }

    return () => {
      if (missileRef.current) clearInterval(missileRef.current);
    };
  }, [isActive, aircraftPos, lat, lon]);

  if (!isActive && phase === 0) return null;

  return (
    <>
      {/* Missile trail */}
      {showMissile && missilePos && aircraftPos && (
        <>
          <Polyline
            positions={[aircraftPos, missilePos]}
            pathOptions={{ color: '#ff2222', weight: 2.5, opacity: 0.8, dashArray: '10 6' }}
          />
          <Polyline
            positions={[missilePos, [lat, lon]]}
            pathOptions={{ color: '#ff6644', weight: 1.5, opacity: 0.3, dashArray: '4 8' }}
          />
          <Marker position={missilePos} icon={L.divIcon({
            className: 'missile-head',
            html: '<div style="width:10px;height:10px;background:#fff;border-radius:50%;box-shadow:0 0 16px #ff4444,0 0 32px #ff2222;"></div>',
            iconSize: [10, 10], iconAnchor: [5, 5],
          })} />
        </>
      )}

      {/* Active explosion animation (phases 1-2): rings + flash */}
      {phase >= 1 && phase <= 2 && (
        <Marker
          position={[lat, lon]}
          icon={createExplosionIcon(attribution, datetime, city)}
          zIndexOffset={2000}
        />
      )}

      {/* Info cartouche: city + source (3 seconds wall-clock) */}
      {showCartouche && (
        <Marker
          position={[lat, lon]}
          icon={createInfoCartoucheIcon(city, sourceInfo, attribution, datetime)}
          zIndexOffset={3000}
        />
      )}

      {/* Persistent strike mark (phase 3+) */}
      {phase >= 3 && (
        <Marker
          position={[lat, lon]}
          icon={createPersistentStrikeIcon(attribution, datetime, city)}
          zIndexOffset={1500}
        />
      )}
    </>
  );
}

// ============ CSS for all explosion animations ============
// ALL durations are in wall-clock seconds (independent of replay speed)
export const EXPLOSION_CSS = `
  .explosion-container { background: none !important; border: none !important; }
  .strike-persistent { background: none !important; border: none !important; }
  .missile-head { background: none !important; border: none !important; }
  .info-cartouche-container { background: none !important; border: none !important; }

  /* ====== EXPLOSION ANIMATION ====== */
  .explosion-marker {
    position: relative;
    width: 300px;
    height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .explosion-flash {
    position: absolute;
    width: 80px; height: 80px;
    border-radius: 50%;
    background: radial-gradient(circle, #ffffff 0%, #fff8e0 30%, rgba(255,100,0,0.6) 60%, transparent 100%);
    animation: flashPulse 0.8s ease-out forwards;
  }

  .explosion-ring1 {
    position: absolute;
    width: 30px; height: 30px;
    border-radius: 50%;
    border: 4px solid rgba(255,68,0,0.9);
    animation: ringExpand 2.0s ease-out forwards;
    box-shadow: 0 0 20px rgba(255,68,0,0.4);
  }
  .explosion-ring2 {
    position: absolute;
    width: 30px; height: 30px;
    border-radius: 50%;
    border: 3px solid rgba(255,136,0,0.7);
    animation: ringExpand 2.5s ease-out 0.3s forwards;
    box-shadow: 0 0 15px rgba(255,136,0,0.3);
  }
  .explosion-ring3 {
    position: absolute;
    width: 30px; height: 30px;
    border-radius: 50%;
    border: 2px solid rgba(255,170,0,0.4);
    animation: ringExpand 3.0s ease-out 0.6s forwards;
  }

  .explosion-core {
    position: absolute;
    width: 20px; height: 20px;
    border-radius: 50%;
    background: radial-gradient(circle, #ff4400, #cc0000);
    box-shadow: 0 0 30px #ff4400, 0 0 60px #ff220088;
    animation: corePulse 2.5s ease-out forwards;
  }

  /* ====== INFO CARTOUCHE ====== */
  .info-cartouche {
    background: rgba(0, 0, 0, 0.92);
    border: 2px solid #ff4400;
    border-radius: 4px;
    min-width: 220px;
    font-family: monospace;
    animation: cartoucheAppear 0.4s ease-out both;
    box-shadow: 0 0 30px rgba(255,68,0,0.4), 0 4px 20px rgba(0,0,0,0.6);
  }
  .info-cartouche-header {
    background: #ff4400;
    color: #fff;
    font-size: 13px;
    font-weight: bold;
    letter-spacing: 2px;
    padding: 4px 12px;
    text-align: center;
  }
  .info-cartouche-icon {
    margin-right: 6px;
  }
  .info-cartouche-body {
    padding: 8px 12px;
  }
  .info-cartouche-city {
    color: #ffffff;
    font-size: 16px;
    font-weight: bold;
    letter-spacing: 1px;
    margin-bottom: 4px;
  }
  .info-cartouche-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }
  .info-cartouche-flag {
    color: #ff8866;
    font-size: 13px;
    font-weight: bold;
  }
  .info-cartouche-time {
    color: #ffaa44;
    font-size: 13px;
    margin-left: auto;
  }
  .info-cartouche-source {
    color: #aaaaaa;
    font-size: 11px;
    border-top: 1px solid rgba(255,255,255,0.1);
    padding-top: 4px;
    margin-top: 2px;
  }

  /* ====== PERSISTENT STRIKE MARK ====== */
  .strike-mark {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .strike-dot {
    width: 14px; height: 14px;
    border-radius: 50%;
    background: #ff3300;
    box-shadow: 0 0 10px #ff4400, 0 0 20px #ff220066;
    animation: strikePulse 2s ease-in-out infinite;
    flex-shrink: 0;
  }
  .strike-info {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(0,0,0,0.9);
    border: 1px solid #ff440099;
    padding: 4px 10px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 12px;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(0,0,0,0.5);
  }

  /* ====== KEYFRAMES (all wall-clock durations) ====== */
  @keyframes flashPulse {
    0% { transform: scale(0.2); opacity: 1; }
    25% { transform: scale(2); opacity: 1; }
    100% { transform: scale(3); opacity: 0; }
  }
  @keyframes ringExpand {
    0% { transform: scale(1); opacity: 0.9; }
    100% { transform: scale(10); opacity: 0; }
  }
  @keyframes corePulse {
    0% { transform: scale(0.3); }
    15% { transform: scale(2); }
    100% { transform: scale(1); opacity: 0.6; }
  }
  @keyframes cartoucheAppear {
    0% { opacity: 0; transform: translateY(10px) scale(0.9); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes strikePulse {
    0%, 100% { box-shadow: 0 0 10px #ff4400, 0 0 20px #ff220066; }
    50% { box-shadow: 0 0 20px #ff4400, 0 0 40px #ff220088; transform: scale(1.3); }
  }
`;
